import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { format } from "date-fns"
import { useState, useEffect } from "react"
import { getPublicBaseUrl } from "./api-config"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export function formatScheduleDate(date: Date | null | undefined): string {
  if (!date) return '';
  
  // Format: "Mon, Mar 16th 4:00pm" (with lowercase am/pm)
  const formatted = format(date, "EEE, MMM do h:mmaaa");
  
  // Convert AM/PM to lowercase
  return formatted.replace(/(am|pm)$/i, (match) => match.toLowerCase());
}

// Check if currently online
export function isOnline(): boolean {
  return typeof navigator !== 'undefined' ? navigator.onLine : true;
}

// Hook to track online/offline status
export function useIsOnline() {
  const [online, setOnline] = useState(isOnline());

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return online;
}

// Convert relative URLs to absolute URLs (for PDF generation)
export function getAbsoluteUrl(url: string): string {
  if (!url) return url;

  // If already absolute, return as is
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }

  // Handle relative URLs starting with /
  if (url.startsWith('/')) {
    return `${getPublicBaseUrl()}${url}`;
  }

  // Handle relative URLs without /
  return `${getPublicBaseUrl()}/${url}`;
}

/**
 * Get the public-facing base URL for creating shareable links
 * This function properly handles native apps by using the server URL
 * that was selected during login, not capacitor://localhost
 *
 * @returns The base URL for public links (e.g., "https://clients.rcrmold.com")
 */
export function getPublicUrl(): string {
  // Use the imported getPublicBaseUrl function (already imported at top of file)
  return getPublicBaseUrl();
}

/**
 * Create a shareable public URL for quotes, invoices, or reports
 * Works correctly in both browser and native apps
 *
 * @param path - The path to append (e.g., "/quotes/abc123" or "quotes/abc123")
 * @returns Full public URL (e.g., "https://clients.rcrmold.com/quotes/abc123")
 *
 * @example
 * // In native app connected to https://clients.rcrmold.com:
 * createPublicUrl('/quotes/abc123')
 * // Returns: "https://clients.rcrmold.com/quotes/abc123"
 *
 * // In browser at localhost:
 * createPublicUrl('/quotes/abc123')
 * // Returns: "http://localhost:5000/quotes/abc123"
 */
export function createPublicUrl(path: string): string {
  const baseUrl = getPublicUrl();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseUrl}${cleanPath}`;
}

export function toTitleCase(s: string): string {
  return s.replace(/\b\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
}

// Strip line breaks from HTML and replace with spaces, preserving bold/italic/underline
export function stripLineBreaksFromHtml(html: string): string {
  if (!html) return '';

  // Create temporary DOM element
  const temp = document.createElement('div');
  temp.innerHTML = html;

  // Replace <br> tags with spaces
  const brTags = temp.querySelectorAll('br');
  brTags.forEach(br => {
    const space = document.createTextNode(' ');
    br.parentNode?.replaceChild(space, br);
  });

  // Replace block-level elements (p, div, h1-h6, lists) with their content + space
  const blockElements = temp.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6, ul, ol, li');
  blockElements.forEach(element => {
    // Get all child nodes (including text and inline elements like <strong>)
    const children = Array.from(element.childNodes);
    const parent = element.parentNode;

    if (parent) {
      // Replace the block element with its children
      children.forEach(child => {
        parent.insertBefore(child, element);
      });

      // Add a space after the content
      parent.insertBefore(document.createTextNode(' '), element);

      // Remove the original block element
      parent.removeChild(element);
    }
  });

  // Get the result and clean up multiple spaces - NO WRAPPER TAG
  let result = temp.innerHTML;
  result = result.replace(/\s+/g, ' ').trim();

  // Return inline HTML only - no <p> wrapper
  return result;
}
